<template>
  <div id="scenarios">
    <el-alert
    :title="message.message"
    :type="message.type" />
    <div class="alternatives" v-if="availability && (availability.availability_before || availability.availability_after)">
      <el-button v-if="availability.availability_before" type="primary" @click="tryAt(availability.availability_before)">Try at {{availabilityBefore}}</el-button>
      <el-button v-if="availability.availability_after" type="primary" @click="tryAt(availability.availability_after)">Try at {{availabilityAfter}}</el-button>
    </div>
    <el-alert
      v-if="error"
      :title="error"
      type="error" />
    <el-collapse v-if="getAvailabilityStatus != 'failure' && getFilteredScenariosStatus != 'failure'" ref="collapse" v-model="activeSections" v-loading="getAvailabilityStatus == 'pending' || getFilteredScenariosStatus == 'pending'" :accordion="isMobileWidth" @change="onSectionChange">
      <el-collapse-item :name="i" v-for="scenario, i in mergedScenarios" :class="{available: scenario && scenario.total_price > 0}">
        <template slot="title">
          <div class="header-content">
            <h3>{{scenario.name}}</h3>
            <div class="price" v-if="scenario.total_price > 0 && settings.default_players_type == 'adults_and_juniors'">
              <i18n-n :value="+scenario.total_price" format="currency"></i18n-n>
            </div>
          </div>
        </template>
        <el-card shadow="never" class="content">
          <div class="description">
            <img :src="scenario.picture_id" v-on:click="selectPackage(scenario)" />
            <div class="description-text">
              <div v-html="scenario.description"></div>
            </div>
          </div>
          <div v-if="scenario.total_price > 0" class="select-package">
            <el-button type="success" v-on:click="selectPackage(scenario)"><span>Select Package</span> <i class="el-icon-arrow-right"></i></el-button>
          </div>
          <div v-else class="availability-message">
            <p>Sorry, this option is only available during the following times:</p>
            <div class="availability-message-content" v-html="scenario.notes_for_web_customers"></div>
          </div>
        </el-card>
      </el-collapse-item>
    </el-collapse>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import format from 'date-fns/format'
import dateMixin from '../mixins/dateMixin'
import mobileMixin from '../mixins/mobileMixin'

export default {
  mixins: [dateMixin, mobileMixin],
  props: ['scenarioFilter', 'date', 'time', 'adults', 'juniors', 'games', 'duration', 'promoCode', 'timedGame', 'bookingType'],
  computed: {
    ...mapState({
      settings: state => state.settings.tenant,
      getFilteredScenariosStatus: state => state.scenarios.status.getFilteredScenarios,
      getAvailabilityStatus: state => state.availability.status.getAvailability,
      createOrderStatus: state => state.order.status.createOrder,
      scenarios: state => state.scenarios.filtered,
      allScenarios: state => state.scenarios.all,
      availability: state => state.availability.all,
      booking: state => state.booking,
    }),
    mergedScenarios() {
      if (this.scenarioFilter.length && !this.scenarios.some((sc) => this.scenarioFilter.indexOf(sc.id) > -1)) {
        return this.allScenarios.filter((sc) => this.scenarioFilter.indexOf(sc.id) > -1)
      }
      return this.scenarios
    },
    availabilityBefore() {
      return this.timeDateForDisplay(this.parseApiDateTime(this.availability.availability_before))
    },
    availabilityAfter() {
      return this.timeDateForDisplay(this.parseApiDateTime(this.availability.availability_after))
    },
    message() {
      if (this.getFilteredScenariosStatus == 'pending' || this.getAvailabilityStatus == 'pending')
        return { type: 'info', message: 'Checking availability...' }

      if (this.getFilteredScenariosStatus == 'failure' || this.getAvailabilityStatus == 'failure')
        return { type: 'error', message: 'Sorry, there was a problem checking availability. Please try again.' }

      let gamesOrDuration = ''
      if (this.settings.default_game_type == 'numbered')
        gamesOrDuration = ', ' + this.games + ' games'
      else if (this.settings.default_game_type == 'timed')
        gamesOrDuration = ', for ' + this.duration + ' minutes'

      let availabilityMessage = 'for ' + this.players + ' players' + gamesOrDuration + ' at ' + this.dateTimeForDisplay(this.dateTime) + '.'

      if (this.availability == false)
        return { type: 'error', message: "We're sorry but we don't have any availability at this time." }

      if (this.scenarios.length == 0)
        return { type: 'error', message: "We're sorry but we don't have any availability at this time." }


      return { type: 'success', message: 'There is availability ' + availabilityMessage }
    },
    players() {
      return parseInt(this.adults) + parseInt(this.juniors)
    },
    ...mapGetters('booking', ['selectedAddonsForApi']),
  },
  watch: {
    getFilteredScenariosStatus() {
      this.activeSections = this.isMobileWidth ? [] : Array.from(this.mergedScenarios.keys())
    },
    createOrderStatus() {
      if (this.createOrderStatus == 'success') {
        this.$router.push('extras')
      } else if (this.createOrderStatus == 'failure') {
        this.error = 'There was a problem updating your order.'
      }
    }
  },
  methods: {
    ...mapActions('booking', ['setDate', 'setTime']),
    tryAt(apiDateTime) {
      const dt = this.parseApiDateTime(apiDateTime)
      this.setDate(this.dateForComponent(dt))
      this.setTime(this.timeForComponent(dt))
    },
    onSectionChange(activeIndex) {
      if (this.isMobileWidth && !isNaN(parseFloat(activeIndex)) && isFinite(activeIndex)) {
        const active = this.$refs.collapse.$el.getElementsByClassName('el-collapse-item')[activeIndex]
        setTimeout(() => {
          active.getElementsByTagName('button')[0].scrollIntoView({ behavior: 'smooth' })
        }, 300)
      }
    },
    selectPackage(scenario) {
      if (scenario.total_price == 0)
        return


      this.error = null
      this.$store.dispatch('order/createOrder', {
        booking_type_id: this.bookingType ? this.bookingType.id : null,
        resource_type: this.bookingType ? this.bookingType.resource_type_id : null,
        date: this.dateForApi(this.dateTime),
        time: this.timeForApi(this.dateTime),
        adults: this.adults,
        juniors: this.juniors,
        timed_game: this.timedGame,
        games: (!this.timedGame ? this.games : null),
        duration: (this.timedGame ? this.duration : null),
        scenario: scenario,
        addons: this.selectedAddonsForApi,
        promotion_code: this.promoCode,
        status: "created"
      })
    },
    update() {
      this.$store.commit('scenarios/setFilteredScenarios', [])
      if (!this.dateTime) return
      if (this.adults == 0 && this.juniors == 0) return
      this.$store.dispatch('scenarios/getFilteredScenarios', {
        resource_type: this.bookingType ? this.bookingType.resource_type_id : null,
        scenario_ids: this.scenarioFilter,
        date: this.dateForApi(this.dateTime),
        time: this.timeForApi(this.dateTime),
				adults: this.adults,
				juniors: this.juniors,
        timed_game: this.timedGame,
        promotion_code: this.promoCode,
        games: (!this.timedGame ? this.games : null),
        duration: (this.timedGame ? this.duration : null)
      })
    },
  },
  created() {
    //this.update()
  },
  data() {
    return {
      error: null,
      activeSections: [],
      showScenarioBookingFormId: null
    }
  }
}
</script>

<style lang="scss" scoped>
  @import "../styles/settings.scss";
  @import "../styles/mixins.scss";

  .availability-message-content {
    white-space: pre;
    font-size: 0.9em;
    h4 {
      margin: 0;
    }
  }

      h3 {
        margin: 0;
        display: inline-block;
        color: #edacac;
        font-family: $heading-font2;
      }
      .price {
        text-align: right;
        float: right;
        font-weight: bold;
        line-height: 28px;
        color: #edacac;
        margin-right: 0.5em;
      }
      .available h3, .available .price {
        color: #fff;
      }

      .header-content {
        width: 100%;
      }

    .el-card {
      img {
        cursor: pointer;
      }

      .select-package {
        display: inline-block;
        width: 25%;
      }
      button {
        @extend button.big;
      }
      img {
        height: auto;
        margin-right: 2em;
      }
      .content {
        display: flex;
        align-items: center;
      }
    }

  .alternatives {
    margin-top: 0.5em;
  }

  .el-alert {
    margin-bottom: 0.5em;
  }
  .el-form {
    display: block;
  }

  @media (min-width: $mobile-max + 1) {
    button {
      height: 4em;
    }
    .description {
      display: table;
      flex: 0 0 75%;
      .description-text, img {
        display: table-cell;
        vertical-align: top;
      }
      .description-text {
        width: 80%;
        padding: 0 2em 0 0;
        white-space: normal;
      }
    }
  }

  .alternatives {
    text-align: center;
    margin-bottom: 0.5em;
    button {
      margin: 0.5em 0;
    }
  }

  @media (max-width: $mobile-max) {


      .header-content {
        h3 {
          display: block;
          float: left;
          font-size: 1em;
        }
      }
      .content {
        display: block;
        text-align: center;

        img {
          width: 100%;
          margin-bottom: 1em;
        }

        .description {
          display: block;
          .description-text {
            padding: 1em 0;
          }
        }

        .select-package {
          margin-top: 0.5em;
          width: 100%;
        }
        button {
          height: 5em;
          width: 100%;
        }
      }
    }
</style>
